import { useEffect, useState } from "react";
import type { ConnectionRequestSummary } from "@shared/schemas/connection-request.schemas";
import type { EventIdPublicOrSlug } from "@shared/schemas/branded-id.schemas";
import { IntroRequestStatus } from "@shared/schemas/intro-request-state.schemas";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";

type PendingAction = "accept" | "decline";

export interface ConnectionRequestCardControllerResult {
  readonly onAccept: () => void;
  readonly onIgnore: () => void;
  readonly isAccepting: boolean;
  readonly isIgnoring: boolean;
}

function needsMarkSeen(status: IntroRequestStatus): boolean {
  return (
    status === IntroRequestStatus.PENDING || status === IntroRequestStatus.SENT
  );
}

/**
 * Per-request card controller. Owns the row-scoped `forConnectionRequest(id)` node
 * so `respond`/`markSeen` carry the prefilled id (mirrors the backend
 * `ConnectionRequestActions`); one instance per row is what lets a list fire
 * per-request mutations without breaking the Rules of Hooks. Marks the request seen
 * once the event resolves — the pending/sent set the old page-controller sweep
 * covered.
 */
export function useConnectionRequestCardController(
  request: ConnectionRequestSummary,
  eventIdPublicOrSlug: EventIdPublicOrSlug,
): ConnectionRequestCardControllerResult {
  const { respond, markSeen, isLoading } = useUserActions()
    .useForEvent({ eventIdPublicOrSlug })
    .useForConnectionRequest(request.idPublic);

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  const isReady = !isLoading;
  const status = request.typeData.status;
  useEffect(() => {
    if (isReady && needsMarkSeen(status)) {
      markSeen.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once the event resolves; markSeen is rebuilt each render so it must not be a dependency
  }, [isReady, status]);

  const onAccept = (): void => {
    setPendingAction("accept");
    respond.mutate({ action: "accept" });
  };

  const onIgnore = (): void => {
    setPendingAction("decline");
    respond.mutate({ action: "decline" });
  };

  return {
    onAccept,
    onIgnore,
    isAccepting: respond.isPending && pendingAction === "accept",
    isIgnoring: respond.isPending && pendingAction === "decline",
  };
}
